// src/pages/stock/Codes.tsx
//
// Codes (Stock Codes)
// - Uses standard table UI (StandardTableToolbar / StandardTableFrame / StandardPagination)
// - URL is the single source of truth for applied filters and page state
// - Separates draft search state from applied search state
// - Supports export via existing search panel
// - Provides inventory risk-based row highlighting
// - Header actions are managed via AppPageHeader slots (Add Stock Code)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { utcToZonedTime } from 'date-fns-tz';

import {
  StockOrder,
  type GetStocksParams,
  type StockResponse,
  exportStocksExcel,
} from '@apis/stock';
import { StockStatus } from '@models/stock';
import {
  getStockStatusLabel,
  getStockStatusMeta,
} from '@constants/stockLabels';

import CodesSearchPannel from '@components/stock/CodesSearchPanel';
import type { SetSearchValueFn } from '@components/common/search';

import { useRegionList } from '@queries/regions/useRegionList';
import { useStockCount } from '@queries/stocks/useStockCount';
import { useStockList } from '@queries/stocks/useStockList';
import { useAllSupplierList } from '@queries/suppliers/useAllSupplierList';

import { KOREAN_TIME_ZONE } from '@constants/timezone';
import { downloadBlob } from '@utils/file';
import { filterSearchParams } from '@utils/search';

import useAsyncEffect from '@hooks/useAsyncEffect';
import { usePageHeader } from '@components/common/pageHeader.context';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

/**
 * Inventory coverage thresholds (days)
 */
const ERROR_DAYS = 7;
const WARNING_DAYS = 14;

type SearchForm = GetStocksParams;
type Option<T> = { label: string; value: T };

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

const parseNumberParam = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const normalizeText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const isValidEnumValue = <T extends string>(
  values: readonly string[],
  v: string | null,
): v is T => {
  if (!v) return false;
  return values.includes(v);
};

/**
 * Reads search parameters from URL and converts them into a SearchForm.
 * This represents the applied (server-side) search state.
 */
const readSearchFromUrl = (sp: URLSearchParams): SearchForm => {
  const statusRaw = sp.get('status');
  const orderingRaw = sp.get('ordering');

  const status = isValidEnumValue<StockStatus>(
    Object.values(StockStatus) as unknown as string[],
    statusRaw,
  )
    ? (statusRaw as StockStatus)
    : undefined;

  const ordering = isValidEnumValue<StockOrder>(
    Object.values(StockOrder) as unknown as string[],
    orderingRaw,
  )
    ? (orderingRaw as StockOrder)
    : undefined;

  return {
    stockCode: sp.get('stockCode') || '',
    stockName: sp.get('stockName') || '',
    regionId: parseNumberParam(sp.get('regionId')),
    supplierId: parseNumberParam(sp.get('supplierId')),
    status,
    ordering,
  };
};

/**
 * Normalizes search parameters before applying them to API calls or URL.
 */
const cleanSearchForm = (form: SearchForm): SearchForm => {
  const stockCode = normalizeText(form.stockCode);
  const stockName = normalizeText(form.stockName);

  return {
    stockCode,
    stockName,
    regionId: typeof form.regionId === 'number' ? form.regionId : undefined,
    supplierId: typeof form.supplierId === 'number' ? form.supplierId : undefined,
    status: form.status || undefined,
    ordering: form.ordering || undefined,
  };
};

const buildUrlParams = (form: SearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);

  const params: Record<string, string> = {};
  if (cleaned.stockCode) params.stockCode = cleaned.stockCode;
  if (cleaned.stockName) params.stockName = cleaned.stockName;
  if (typeof cleaned.regionId === 'number') params.regionId = String(cleaned.regionId);
  if (typeof cleaned.supplierId === 'number') params.supplierId = String(cleaned.supplierId);
  if (cleaned.status) params.status = String(cleaned.status);
  if (cleaned.ordering) params.ordering = String(cleaned.ordering);

  return {
    ...filterSearchParams(params),
    page: String(page),
  };
};

const formatNumber = (n: unknown) => {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return '-';
  return v.toLocaleString();
};

const toSafeNumber = (v: unknown, fallback = 0) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

type StockRiskLevel = 'normal' | 'warning' | 'error';
type StockRisk = { level: StockRiskLevel; coverageDays: number; avgDaily: number };

/**
 * Calculates inventory risk based on remaining stock and last-week shipment volume.
 */
const calcStockRisk = (
  remainedCount: unknown,
  lastWeekShippedCount: unknown,
): StockRisk => {
  const remained = toSafeNumber(remainedCount, 0);
  const shippedWeek = toSafeNumber(lastWeekShippedCount, 0);

  if (remained <= 0) {
    return {
      level: 'error',
      coverageDays: 0,
      avgDaily: shippedWeek > 0 ? shippedWeek / 7 : 0,
    };
  }

  if (shippedWeek <= 0) {
    return { level: 'normal', coverageDays: Infinity, avgDaily: 0 };
  }

  const avgDaily = shippedWeek / 7;
  const coverageDays = avgDaily > 0 ? remained / avgDaily : Infinity;

  if (coverageDays <= ERROR_DAYS) return { level: 'error', coverageDays, avgDaily };
  if (coverageDays <= WARNING_DAYS) return { level: 'warning', coverageDays, avgDaily };
  return { level: 'normal', coverageDays, avgDaily };
};

const formatCoverage = (coverageDays: number) => {
  if (!Number.isFinite(coverageDays)) return '∞';
  if (coverageDays <= 0) return '0';
  return coverageDays.toFixed(1);
};

/**
 * Shallow comparison for detecting meaningful differences between search forms.
 * Used to avoid overwriting draft state unnecessarily.
 */
const isSameSearchForm = (a: SearchForm, b: SearchForm) => {
  const aa = cleanSearchForm(a);
  const bb = cleanSearchForm(b);

  return (
    (aa.stockCode ?? '') === (bb.stockCode ?? '') &&
    (aa.stockName ?? '') === (bb.stockName ?? '') &&
    (aa.regionId ?? null) === (bb.regionId ?? null) &&
    (aa.supplierId ?? null) === (bb.supplierId ?? null) &&
    (aa.status ?? null) === (bb.status ?? null) &&
    (aa.ordering ?? null) === (bb.ordering ?? null)
  );
};

const Codes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setPageHeaderSlots } = usePageHeader();

  /**
   * appliedSearch:
   *   Represents the active filters synced with URL and API calls.
   * draftSearch:
   *   Represents the editable state inside the search panel UI.
   */
  const [appliedSearch, setAppliedSearch] = useState<SearchForm>(() =>
    readSearchFromUrl(searchParams),
  );
  const [draftSearch, setDraftSearch] = useState<SearchForm>(() => ({
    ...readSearchFromUrl(searchParams),
  }));

  const [page, setPage] = useState<number>(() => {
    const raw = searchParams.get('page');
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });

  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * Synchronizes component state from URL parameters.
   */
  useEffect(() => {
    const nextApplied = readSearchFromUrl(searchParams);

    const raw = searchParams.get('page');
    const n = Number(raw);
    const nextPage = Number.isFinite(n) && n > 0 ? n : 1;

    setAppliedSearch(nextApplied);
    setDraftSearch((prev) =>
      isSameSearchForm(prev, nextApplied) ? prev : nextApplied,
    );
    setPage(nextPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const cleanedApplied = useMemo(
    () => cleanSearchForm(appliedSearch),
    [appliedSearch],
  );

  // Queries
  const { data, isLoading } = useStockList({ page, searchParam: cleanedApplied });
  const { data: totalCount, isLoading: isLoadingCount } =
    useStockCount(cleanedApplied);

  const safeTotalCount = totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE));
  const rows: StockResponse[] = (data?.data ?? []) as StockResponse[];

  const isTableLoading = Boolean(isLoading || isLoadingCount);

  // Options
  const { data: regionsData } = useRegionList();
  const { data: suppliersData } = useAllSupplierList();

  const [regionOptions, setRegionOptions] = useState<Array<Option<number>>>([]);
  const [supplierOptions, setSupplierOptions] = useState<Array<Option<number>>>([]);

  useAsyncEffect(async () => {
    if (!regionsData) return;
    setRegionOptions(
      (regionsData.data ?? []).map((r) => ({
        label: r.regionCode,
        value: r.regionId,
      })),
    );
  }, [regionsData]);

  useAsyncEffect(async () => {
    if (!suppliersData) return;
    setSupplierOptions(
      (suppliersData.data ?? []).map((s) => ({
        label: `${s.supplierName} (${s.supplierCode})`,
        value: s.supplierId,
      })),
    );
  }, [suppliersData]);

  const statusOptions = useMemo<Array<Option<StockStatus>>>(() => {
    const values = Object.values(StockStatus) as StockStatus[];
    return values.map((v) => ({ value: v, label: getStockStatusLabel(v) }));
  }, []);

  const selectedRegion = useMemo(() => {
    const id = draftSearch.regionId;
    if (typeof id !== 'number') return null;
    return regionOptions.find((o) => o.value === id) ?? null;
  }, [draftSearch.regionId, regionOptions]);

  const selectedSupplier = useMemo(() => {
    const id = draftSearch.supplierId;
    if (typeof id !== 'number') return null;
    return supplierOptions.find((o) => o.value === id) ?? null;
  }, [draftSearch.supplierId, supplierOptions]);

  const selectedStatus = useMemo(() => {
    const v = draftSearch.status as StockStatus | undefined;
    if (!v) return null;
    return statusOptions.find((o) => o.value === v) ?? null;
  }, [draftSearch.status, statusOptions]);

  /**
   * Updates draft search values from the shared search component.
   */
  const setValue: SetSearchValueFn<SearchForm> = useCallback((key, value) => {
    setDraftSearch((prev) => {
      const next = { ...prev } as Record<string, unknown>;

      switch (key as keyof SearchForm) {
        case 'stockCode':
        case 'stockName':
          next[key] = typeof value === 'string' ? value : '';
          break;

        case 'regionId':
        case 'supplierId':
          next[key] = typeof value === 'number' ? value : undefined;
          break;

        case 'status':
        case 'ordering':
          next[key] = value ?? undefined;
          break;

        default:
          next[key as string] = value;
          break;
      }

      return next as SearchForm;
    });
  }, []);

  // Apply / Reset / Pagination
  const applyFilters = useCallback(
    (nextDraft?: SearchForm) => {
      const next = cleanSearchForm(nextDraft ?? draftSearch);

      setAppliedSearch(next);
      setPage(1);
      setSearchParams(buildUrlParams(next, 1));
    },
    [draftSearch, setSearchParams],
  );

  const handleSearch = useCallback(() => applyFilters(), [applyFilters]);

  const handleReset = useCallback(() => {
    const cleared: SearchForm = {
      stockCode: '',
      stockName: '',
      regionId: undefined,
      supplierId: undefined,
      status: undefined,
      ordering: undefined,
    };

    setDraftSearch(cleared);
    applyFilters(cleared);
  }, [applyFilters]);

  const handleChangePage = (next: number) => {
    const safeNext = clamp(next, 1, totalPages);
    setPage(safeNext);
    setSearchParams(buildUrlParams(appliedSearch, safeNext));
  };

  // Export
  const [isExporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting) return;

    setExporting(true);
    try {
      const nowUtc = new Date();
      const kstDate = utcToZonedTime(nowUtc, KOREAN_TIME_ZONE);

      const res = await exportStocksExcel(cleanedApplied);
      const fileName = `stock-codes-${kstDate.toISOString()}.xlsx`;
      downloadBlob(res, fileName);
    } finally {
      setExporting(false);
    }
  }, [cleanedApplied, isExporting]);

  // Navigation
  const handleOnAdd = useCallback(() => {
    navigate('/stocks/codes/new');
  }, [navigate]);

  const handleOpenEdit = useCallback(
    (stockId: number) => {
      navigate(`/stocks/codes/${stockId}/edit`);
    },
    [navigate],
  );

  /**
   * Registers page-level header actions.
   * Actions are explicitly cleared on unmount to avoid leakage.
   */
  useEffect(() => {
    setPageHeaderSlots({
      actions: [
        {
          key: 'add',
          label: 'Add Stock Code',
          startIcon: <AddIcon />,
          onClick: handleOnAdd,
          variant: 'contained',
          primary: true,
          disabled: isTableLoading,
        },
      ],
    });

    return () => setPageHeaderSlots({});
  }, [setPageHeaderSlots, handleOnAdd, isTableLoading]);

  const columnsCount = 11;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box mt={2}>
        <CodesSearchPannel
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          draftSearch={draftSearch}
          setValue={setValue}
          regionOptions={regionOptions}
          supplierOptions={supplierOptions}
          statusOptions={statusOptions}
          selectedRegion={selectedRegion}
          selectedSupplier={selectedSupplier}
          selectedStatus={selectedStatus}
          onSearch={handleSearch}
          onReset={handleReset}
          onExport={handleExport}
          exportLoading={isExporting}
          autoSearch={PAGE_AUTO_SEARCH}
        />
      </Box>

      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={safeTotalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      {(isTableLoading || isExporting) && (
        <Box mt={1}>
          <LinearProgress />
        </Box>
      )}

      <Box mt={1}>
        <StandardTableFrame<StockResponse>
          containerSx={{ borderRadius: 2 }}
          minWidth={1300}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No stock codes found."
          getRowStatus={(row) => {
            if (row.status === StockStatus.INACTIVE) return 'inactive';
            if (!row.isInventoryManaged) return 'normal';

            const risk = calcStockRisk(
              row.remainedCount,
              row.lastWeekShippedCount,
            );
            if (risk.level === 'error') return 'error';
            if (risk.level === 'warning') return 'warning';
            return 'normal';
          }}
          head={
            <TableRow>
              <TableCell width={80} align="right">#</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Stock Code</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Stock Name</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Supplier</TableCell>
              <TableCell width={120}>Region</TableCell>
              <TableCell width={120} align="right">Remained</TableCell>
              <TableCell width={160} align="right">Shipped (Last 1 week)</TableCell>
              <TableCell width={140} align="right">Shipped (Total)</TableCell>
              <TableCell width={120} align="right">Refunded</TableCell>
              <TableCell width={120} align="right">Expired</TableCell>
              <TableCell width={140} align="center">Status</TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.stockId)}
          renderRow={(row, index) => {
            const stockId = Number(row.stockId);
            const remained = toSafeNumber(row.remainedCount, 0);
            const shippedWeek = toSafeNumber(row.lastWeekShippedCount, 0);
            const statusMeta = getStockStatusMeta(row.status);

            const risk = calcStockRisk(remained, shippedWeek);
            const coverageLabel = formatCoverage(risk.coverageDays);

            const riskText =
              row.status === StockStatus.INACTIVE
                ? 'INACTIVE'
                : risk.level === 'error'
                  ? `ERROR (≤ ${ERROR_DAYS} days)`
                  : risk.level === 'warning'
                    ? `WARNING (≤ ${WARNING_DAYS} days)`
                    : 'NORMAL';

            const tooltip = (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>
                  {riskText}
                </Typography>
                {row.status !== StockStatus.INACTIVE && (
                  <>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Coverage: {coverageLabel} days
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Avg/day: {risk.avgDaily ? risk.avgDaily.toFixed(2) : '0.00'}
                    </Typography>
                  </>
                )}
              </Box>
            );

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleOpenEdit(stockId)}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      fontWeight: 800,
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {(page - 1) * PAGE_SIZE + index + 1}
                  </Button>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleOpenEdit(stockId)}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      fontWeight: 800,
                      textTransform: 'none',
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {row.stockCode || '-'}
                  </Button>
                </TableCell>

                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {row.stockName || '-'}
                  </Typography>
                </TableCell>

                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2" noWrap title={row.supplier?.supplierName ?? ''}>
                    {row.supplier?.supplierName ?? '-'}
                  </Typography>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>{row.region?.regionCode ?? '-'}</TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  <Tooltip title={tooltip} arrow>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {formatNumber(remained)}
                    </Typography>
                  </Tooltip>
                </TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  <Tooltip title={tooltip} arrow>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {formatNumber(shippedWeek)}
                    </Typography>
                  </Tooltip>
                </TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  {formatNumber(row.totalShippedCount ?? 0)}
                </TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  {formatNumber(row.refundedCount ?? 0)}
                </TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  {formatNumber(row.expiredCount ?? 0)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    size="small"
                    label={statusMeta.label}
                    color={statusMeta.chip.color}
                    variant={statusMeta.chip.variant}
                  />
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={safeTotalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default Codes;
